import { QueryClient, useQuery, useQueryClient } from '@tanstack/react-query';
import { useAuth } from '@/auth/useAuth';
import { fetchOttoApps } from '@/services/resourceGraph';
import { apiVersions, armEndpoint, getLogicEndpoint } from '@/services/endpoints';
import { isLocalMode, getLocalMockApps } from '@/services/localRuntimeClient';
import { AutomationApp, AutomationAppUIModel, AzureContainerApp } from '@/types/automation';

// Polling interval for transitional app states (e.g. Deleting, Provisioning).
// Kept short enough to feel responsive but slow enough to avoid hammering the
// control plane.
export const APPS_TRANSITION_POLL_MS = 5000;

// Provisioning states that indicate an in-flight operation and therefore
// require the list to be re-queried until the operation settles. Compared
// case-insensitively against `properties.provisioningState`.
const TRANSITIONAL_PROVISIONING_STATES = new Set([
  'deleting',
  'deleted',
  'creating',
  'accepted',
  'provisioning',
  'updating',
  'running',
  'inprogress',
]);

export const isTransitionalAppState = (state: string | null | undefined): boolean => {
  if (!state) return false;
  return TRANSITIONAL_PROVISIONING_STATES.has(state.toLowerCase());
};

const hasAnyTransitionalApp = (apps: AutomationAppUIModel[] | undefined): boolean => {
  if (!apps) return false;
  return apps.some((app) => isTransitionalAppState(app.properties?.provisioningState));
};

export const useApps = (subscriptionId?: string | null) => {
  const { getArmToken, isAuthenticated } = useAuth();

  return useQuery({
    queryKey: ['apps', subscriptionId ?? 'all'],
    queryFn: async () => {
      const token = await getArmToken();
      if (!token) {
        throw new Error('Failed to acquire ARM token');
      }
      const subscriptionIds = subscriptionId ? [subscriptionId] : undefined;
      return fetchOttoApps(token, subscriptionIds);
    },
    enabled: isAuthenticated,
    staleTime: 5 * 60 * 1000, // 5 minutes
  });
};

export const useAppsInProject = (projectName: string) => {
  const { getOttoToken, isAuthenticated } = useAuth();

  return useQuery({
    queryKey: ['appsInProject', projectName.toLowerCase()],
    queryFn: async (): Promise<AutomationAppUIModel[]> => {
      if (isLocalMode()) {
        // In local mode, fetch apps from the mock control plane
        try {
          const endpoint = getLogicEndpoint();
          const response = await fetch(`${endpoint}/automationProjects/${projectName}/applications?api-version=${apiVersions.otto}`);
          if (response.ok) {
            const data = await response.json();
            return data.value?.map((app: AutomationApp) => ({ ...app, name: app.name.split('/').slice(-1)[0], projectName })) ?? [];
          }
        } catch {
          // Network error — fall through to fallback
        }
        // Fallback to project-scoped mock apps
        return getLocalMockApps(projectName);
      }

      const token = await getOttoToken();
      if (!token) {
        throw new Error('Failed to acquire Otto token');
      }

      const response = await fetch(`${getLogicEndpoint()}/automationProjects/${projectName}/applications?api-version=${apiVersions.otto}`, {
        method: 'GET',
        headers: {
          'Authorization': `Bearer ${token}`,
          'Content-Type': 'application/json',
        },
      });

      if (!response.ok) {
          const errorText = await response.text();
          throw new Error(`Failed to fetch applications for project ${projectName}: ${response.status} ${errorText}`);
      }
      const data = await response.json();
      // Ensure we always return an array (React Query requires non-undefined)
      return data.value?.map((app: AutomationApp) => ({ ...app, name: app.name.split('/').slice(-1)[0], projectName})) ?? [];
    },
    enabled: (isAuthenticated || isLocalMode()) && !!projectName,
    staleTime: 5 * 60 * 1000, // 5 minutes
    // Poll while any app is in a transitional state (Deleting, Provisioning,
    // etc.) so the UI reflects the resource's true state without forcing the
    // user to refresh manually. 
    refetchInterval: (query) =>
      hasAnyTransitionalApp(query.state.data) ? APPS_TRANSITION_POLL_MS : false,
    refetchIntervalInBackground: false,
  });
};

export const useAppsFromIds = (appIds: string[]) => {
  const { getArmToken, isAuthenticated } = useAuth();
  const queryClient = useQueryClient();

  return useQuery({
    queryKey: ['apps', appIds.join(',').toLowerCase()],
    queryFn: async () => {
      const token = await getArmToken();
      if (!token) {
        throw new Error('Failed to acquire ARM token');
      }
      const promises = appIds.map((id) => getAppFromId(queryClient, id, getArmToken));
      return Promise.all(promises);
    },
    enabled: isAuthenticated,
    staleTime: 5 * 60 * 1000, // 5 minutes
  });
};

export const getAppFromId = async (queryClient: QueryClient, appId: string, getToken: () => Promise<string | null>): Promise<AzureContainerApp | null> => {
  return queryClient.fetchQuery({
    queryKey: ['containerapp', appId.toLowerCase()],
    queryFn: async () => {
      const token = await getToken();
      if (!token) {
        throw new Error('Failed to acquire ARM token');
      }

      const response = await fetch(`${armEndpoint}${appId}?api-version=2024-03-01`, {
        method: 'GET',
        headers: {
          'Authorization': `Bearer ${token}`,
          'Content-Type': 'application/json',
        },
      });

      if (!response.ok) {
        const errorText = await response.text();
        throw new Error(`Failed to fetch application ${appId}: ${response.status} ${errorText}`);
      }

      const data = await response.json();
      return data;
    },
    staleTime: 5 * 60 * 1000, // 5 minutes
  });
};

